import { Route } from '@angular/router';
import { DrawerComponent } from '@lassi/components';
import {
  advancedAddressSearchFormDataResolver,
  baseLayerGuard,
  IdentifyFeatureType,
  presetGuard,
  RouteUtilsService,
  searchAddressForLocalityGuard,
} from '@lassi/webapp-shared';
import { AerialPhotographyStore } from './ohap/aerial-photography.store';
import {
  ParcelSelectStore,
  SPEAR_SELECT_ADVANCED_SEARCH_OPTIONS_PROVIDER,
} from './spear/spear';

const drawerRoute: Route = {
  path: RouteUtilsService.MAP_MENU_ROOT_PATH,
  outlet: RouteUtilsService.MAP_MENU_OUTLET_NAME,
  component: DrawerComponent,
};

const layersRoutes: Route[] = [
  {
    path: 'layers',
    children: [
      {
        path: '',
        loadComponent: () =>
          import(
            '@lassi/webapp-routes/layer-management/layer-management.component'
          ).then((c) => c.LayerManagementComponent),
        data: {
          title: 'Layers',
          bottomSheetBack: '../landing',
        },
      },
      {
        path: 'layer-properties/:name',
        loadComponent: () =>
          import(
            '@lassi/webapp-routes/layer-management/layer-properties.component'
          ).then((c) => c.LayerPropertiesComponent),
        data: {
          title: 'Layer Properties',
          bottomSheetBack: 'layers',
          drawerBack: 'layers',
        },
      },
    ],
  },
];

const ohapRoutes: Route[] = [
  {
    path: 'photographs',
    children: [
      {
        path: 'selected',
        loadComponent: () =>
          import('./ohap/selected-photographs.component').then(
            (c) => c.SelectedPhotographsComponent,
          ),
        data: {
          title: 'Selected Photographs',
        },
      },
      {
        path: ':boundingBox',
        loadComponent: () =>
          import('./ohap/identify-photographs.component').then(
            (c) => c.IdentifyPhotographsComponent,
          ),
        data: {
          title: 'Select Photographs',
        },
      },
    ],
  },
];

const spearRoutes: Route[] = [
  {
    path: 'selected',
    loadComponent: () =>
      import('./spear/selected-parcels.component').then(
        (c) => c.SelectedParcelsComponent,
      ),
    data: {
      title: 'Selected Parcels',
    },
  },
  {
    path: 'identify',
    runGuardsAndResolvers: 'paramsOrQueryParamsChange',
    children: [
      {
        path: `${IdentifyFeatureType.Parcel}/:vPfis`,
        loadComponent: () =>
          import('./spear/identify-parcel.component').then(
            (c) => c.IdentifyParcelComponent,
          ),
      },
    ],
  },
  {
    path: 'search',
    children: [
      {
        path: '',
        loadComponent: () =>
          import(
            '@lassi/webapp-routes/advanced-search/advanced-search-home.component'
          ).then((c) => c.AdvancedSearchHomeComponent),
        data: {
          title: 'Advanced Search',
        },
      },
      {
        path: 'address',
        children: [
          {
            path: '',
            loadComponent: () =>
              import(
                '@lassi/webapp-routes/advanced-search/address/address-form-advanced.component'
              ).then((c) => c.AddressFormAdvancedComponent),
            data: {
              title: 'Address Search',
              drawerBack: 'search',
            },
          },
          {
            path: 'results',
            children: [
              {
                path: 'locality',
                canActivate: [searchAddressForLocalityGuard],
                loadComponent: () =>
                  import(
                    '@lassi/webapp-routes/advanced-search/address/locality-search-results.component'
                  ).then((c) => c.LocalitySearchResultsComponent),
                resolve: {
                  formData: advancedAddressSearchFormDataResolver,
                },
                data: {
                  title: 'Results',
                  drawerBack: 'search/address',
                },
              },
              {
                path: 'road',
                loadComponent: () =>
                  import(
                    '@lassi/webapp-routes/advanced-search/address/road-search-results.component'
                  ).then((c) => c.RoadSearchResultsComponent),
                data: {
                  title: 'Results',
                  drawerBack: 'search/address',
                },
              },
              {
                path: 'road-properties',
                loadComponent: () =>
                  import(
                    '@lassi/webapp-routes/advanced-search/address/road-search-property-results.component'
                  ).then((c) => c.RoadSearchPropertyResultsComponent),
                data: {
                  title: 'Results',
                  drawerBack: 'search/address/results/road',
                },
              },
              {
                path: 'address',
                loadComponent: () =>
                  import(
                    '@lassi/webapp-routes/advanced-search/address/property-results.component'
                  ).then((c) => c.PropertyResultsComponent),
                data: {
                  title: 'Results',
                  drawerBack: 'search/address',
                },
              },
            ],
          },
        ],
      },
      {
        path: 'crown-description',
        children: [
          {
            path: '',
            loadComponent: () =>
              import(
                '@lassi/webapp-routes/advanced-search/crown-description/crown-description-search.component'
              ).then((c) => c.CrownDescriptionSearchComponent),
            data: {
              title: 'Crown Description Search',
              drawerBack: 'search',
            },
          },
          {
            path: 'results',
            loadComponent: () =>
              import(
                '@lassi/webapp-routes/advanced-search/crown-description/crown-description-search-results.component'
              ).then((c) => c.CrownDescriptionSearchResultsComponent),
            data: {
              title: 'Crown Description Search',
              drawerBack: 'search/crown-description',
            },
          },
        ],
      },
      {
        path: 'lot-plan',
        children: [
          {
            path: '',
            loadComponent: () =>
              import(
                '@lassi/webapp-routes/advanced-search/lot/lot-search-plan.component'
              ).then((c) => c.LotSearchPlanComponent),
            data: {
              title: 'Lot Search',
              drawerBack: 'search',
            },
          },
          {
            path: 'results',
            loadComponent: () =>
              import(
                '@lassi/webapp-routes/advanced-search/lot/lot-search-plan-results.component'
              ).then((c) => c.LotSearchPlanResultsComponent),
            data: {
              title: 'Results',
              drawerBack: 'search/lot-plan',
            },
          },
        ],
      },
    ],
  },
];

export const appRoutes: Route[] = [
  {
    path: 'tpc',
    canActivate: [baseLayerGuard, presetGuard],
    loadComponent: () =>
      import('./tpc/tpc.component').then((c) => c.TpcComponent),
  },
  {
    path: 'vmt',
    canActivate: [baseLayerGuard, presetGuard],
    loadComponent: () =>
      import('./vmt/vmt-page.component').then((c) => c.VmtPageComponent),
    children: [
      {
        ...drawerRoute,
        children: [...layersRoutes],
      },
    ],
  },
  {
    path: 'ohap',
    canActivate: [baseLayerGuard, presetGuard],
    loadComponent: () =>
      import('./ohap/ohap-page.component').then((c) => c.OhapPageComponent),
    children: [
      {
        ...drawerRoute,
        children: [...layersRoutes, ...ohapRoutes],
      },
    ],
    providers: [AerialPhotographyStore],
  },
  {
    path: 'spear',
    canActivate: [baseLayerGuard, presetGuard],
    loadComponent: () =>
      import('./spear/spear-page.component').then((c) => c.SpearPageComponent),
    children: [
      {
        ...drawerRoute,
        children: [...layersRoutes, ...spearRoutes],
      },
    ],
    providers: [
      SPEAR_SELECT_ADVANCED_SEARCH_OPTIONS_PROVIDER,
      ParcelSelectStore,
    ],
  },
  {
    path: '',
    loadComponent: () =>
      import('./test-page.component').then((c) => c.TestPageComponent),
  },
  {
    path: '**',
    redirectTo: '',
  },
  {
    path: '**',
    redirectTo: 'rLassi',
  },
];
